import {
  Icon,
  Snackbar,
  SnackbarProps,
  Theme,
  useTheme,
} from "@outschool/backpack";
import { faTimes } from "@outschool/icons";
import React, { createContext, useCallback, useContext, useState } from "react";

type AnchorOrigin = SnackbarProps["anchorOrigin"];

type SnackbarContextType = {
  addSnackbarContent: (
    content: React.ReactNode,
    durationMs?: number,
    backgroundColor?: string,
    anchorOrigin?: AnchorOrigin,
    contentSx?: Record<string, unknown>,
  ) => void;
  handleSnackbarClose?: (event: React.SyntheticEvent | Event) => void;
};

export const SnackbarContext = createContext<SnackbarContextType>({
  addSnackbarContent: () => {},
  handleSnackbarClose: () => {},
});

const DEFAULT_ANCHOR_ORIGIN: AnchorOrigin = {
  vertical: "bottom",
  horizontal: "left",
};

export const SnackbarProvider: React.FC<React.PropsWithChildren<{}>> = ({
  children,
}) => {
  const theme = useTheme();

  const [snackbarData, setSnackbarData] = useState<{
    isOpen: boolean;
    content?: React.ReactNode;
    durationMs: number;
    backgroundColor?: string;
    anchorOrigin: AnchorOrigin;
    contentSx?: Record<string, unknown>;
  }>({
    isOpen: false,
    content: undefined,
    durationMs: 0,
    backgroundColor: "success",
    anchorOrigin: DEFAULT_ANCHOR_ORIGIN,
  });

  const addSnackbarContent = (
    content: React.ReactNode,
    durationMs: number = 60000,
    backgroundColor: string = "success",
    anchorOrigin: AnchorOrigin = DEFAULT_ANCHOR_ORIGIN,
    contentSx?: Record<string, unknown>,
  ) => {
    setSnackbarData({
      isOpen: true,
      content,
      durationMs,
      anchorOrigin,
      contentSx,
      backgroundColor:
        backgroundColor === "success"
          ? theme.palette.success.dark
          : backgroundColor === "error"
          ? "error.500"
          : backgroundColor,
    });
  };

  const handleSnackbarClose = useCallback(
    (_event: React.SyntheticEvent | Event) => {
      setSnackbarData(prev => ({
        ...prev,
        isOpen: false,
        content: undefined,
        durationMs: 0,
      }));
    },
    [],
  );

  return (
    <SnackbarContext.Provider
      value={{ addSnackbarContent, handleSnackbarClose }}
    >
      {children}
      <Snackbar
        open={snackbarData.isOpen}
        autoHideDuration={snackbarData.durationMs}
        onClose={handleSnackbarClose}
        anchorOrigin={snackbarData.anchorOrigin}
        message={snackbarData.content}
        action={
          <Icon
            onClick={handleSnackbarClose}
            icon={faTimes}
            size="medium"
            sx={{ cursor: "pointer" }}
          />
        }
        sx={(theme: Theme) => ({
          [theme.breakpoints.down("md")]: {
            // On mobile, move the Snackbar up to avoid covering mobile footer, and add horizontal margin to reduce width
            "&.MuiSnackbar-root": { bottom: "72px", margin: "0 16px" },
          },
          "& .MuiSnackbarContent-root": {
            color: "white",
            backgroundColor: snackbarData.backgroundColor,
            flexWrap: "nowrap",
            alignItems: "center",
            ...snackbarData.contentSx,
          },
        })}
      />
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => useContext(SnackbarContext);
